// Stable deploy identifier for cache-busting. Evaluated once at module load.
// When REACT_APP_DEPLOY_ID is set in the build pipeline (e.g. Firebase hosting),
// images are busted per-deploy. When absent, no cache-buster is appended so the
// browser can cache images across sessions.
const CACHE_BUSTER: string =
  ((typeof process !== 'undefined' && (process as any).env && (process as any).env.REACT_APP_DEPLOY_ID) ||
    (typeof window !== 'undefined' && (window as any).__DEPLOY_ID) ||
    '') as string;

function appendCacheBuster(path: string): string {
  if (!CACHE_BUSTER) return path;
  // Avoid duplicating the cache-busting param if already present
  if (/([?&])v=/.test(path)) return path;
  return path.includes("?") ? `${path}&v=${CACHE_BUSTER}` : `${path}?v=${CACHE_BUSTER}`;
}

export function normalizePublicImageUrl(input?: string | null): string | undefined {
  if (!input) return input ?? undefined;

  try {
    // Already a relative public image path
    if (input.startsWith("/images/public/")) return appendCacheBuster(input);
    if (input.startsWith("images/public/")) return appendCacheBuster(`/${input}`);

    // If absolute URL
    if (/^https?:\/\//i.test(input)) {
      const url = new URL(input);
      // Case 1: Already pointing at our public images route
      const idx = url.pathname.indexOf("/images/public/");
      if (idx !== -1) {
        // For localhost URLs (development), keep the full absolute URL to point to backend
        if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
          return appendCacheBuster(input);
        }
        // For production URLs, strip host to use relative path
        return appendCacheBuster(url.pathname.substring(idx));
      }
      // Case 2: GCS bucket path → convert to /images/public/{cleanPath}
      // Example: https://storage.googleapis.com/pedagogue-lesson-content/level-1/lesson-1/foo.png
      if (
        /storage.googleapis.com/i.test(url.host) &&
        url.pathname.includes("pedagogue-lesson-content/")
      ) {
        const afterBucket = url.pathname.split("pedagogue-lesson-content/")[1] || "";
        // Strip lesson-X/ segment if present
        const cleanPath = afterBucket.replace(/lesson-\d+\//, "");
        return appendCacheBuster(`/images/public/${cleanPath}`);
      }
      // Otherwise leave absolute URLs as-is
      return input;
    }

    // If path contains images/public somewhere, normalize from that segment
    const idx = input.indexOf("images/public/");
    if (idx !== -1) {
      const suffix = input.substring(idx);
      const normalized = suffix.startsWith("/") ? suffix : `/${suffix}`;
      return appendCacheBuster(normalized);
    }

    // If the input is a bare handle like "level-1/foo/bar" ensure prefix
    if (/^(level-\d+\/.+)/.test(input)) {
      return appendCacheBuster(`/images/public/${input}`);
    }
    // If it's a likely relative path with extension (e.g., "icons/foo.png"), prefix conservatively
    if (!input.startsWith("/") && /\.(png|jpg|jpeg|svg)$/i.test(input)) {
      return appendCacheBuster(`/images/public/${input}`);
    }

    // Otherwise, return as-is
    return input;
  } catch {
    return input || undefined;
  }
}


